import React from 'react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  DimensionValue,
  DimensionWithEmployeeAssignment,
  MutipleValueDimensionAssignment,
  SingleValueDimensionAssignment,
  UNASSIGNED,
} from 'modules/finance/hooks/useEmployeeDimensionsAssignments'

type SaveSingleValueAssignment = (
  employeeId: string,
  assignment: SingleValueDimensionAssignment
) => Promise<boolean>

type SaveMultipleValueAssignment = (
  employeeId: string,
  assignment: MutipleValueDimensionAssignment
) => Promise<boolean>

export interface Props {
  open: boolean
  dimensionsAssignments: DimensionWithEmployeeAssignment[]
  saveAssignment: SaveSingleValueAssignment | SaveMultipleValueAssignment
  endAssignment?: (membershipId: string) => Promise<boolean>
  employeeId: string
  legalEntityId: string
  employeeFullName: string
  onClose: () => void
  refetch: () => Promise<unknown>
}

const useAssignmentsPanel = ({
  dimensionsAssignments,
  employeeId,
  saveAssignment,
  endAssignment,
  onClose,
  refetch,
}: Props) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const [assignments, setAssignments] = React.useState([...dimensionsAssignments])

  useUnsafeEffect(() => {
    setAssignments([...dimensionsAssignments])
  }, [dimensionsAssignments])

  const updatedAssignments = assignments.filter((next) => {
    const prev = dimensionsAssignments.find((d) => d.id == next.id)

    if (!prev) return false

    if (prev.type == 'single-value' && next.type == 'single-value') {
      if (next.assignmentValue?.id === UNASSIGNED) {
        return false
      }
      return prev.assignmentValue?.id != next.assignmentValue?.id
    }
    if (prev.type == 'multiple-value' && next.type == 'multiple-value') {
      if (prev.assignments.length != next.assignments.length) {
        return true
      }
      return prev.assignments.some((p) => {
        const n = next.assignments.find((v) => v.id == p.id)
        return !n || p.percentage != n.percentage
      })
    }
  })

  const endedAssignments = assignments.filter((next) => {
    const prev = dimensionsAssignments.find((d) => d.id == next.id)
    if (!prev) return false

    if (prev.type == 'single-value' && next.type == 'single-value') {
      return next.assignmentValue?.id === UNASSIGNED
    }
  })

  const onSave = async () => {
    setSubmitting(true)

    const operations: Promise<boolean>[] = []

    const saveAssignmentByType = (assignment: DimensionWithEmployeeAssignment) => {
      if (assignment.type === 'single-value') {
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        const save = saveAssignment as SaveSingleValueAssignment

        return save(employeeId, assignment)
      } else {
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        const save = saveAssignment as SaveMultipleValueAssignment

        return save(employeeId, assignment)
      }
    }

    if (updatedAssignments.length > 0) {
      updatedAssignments
        .filter((a) => !endedAssignments.includes(a))
        .forEach((newAssignment) => {
          const op = saveAssignmentByType(newAssignment)

          if (op) operations.push(op)
        })
    }

    if (endedAssignments.length > 0) {
      endedAssignments.forEach((assignment) => {
        if ('membershipId' in assignment && assignment.membershipId) {
          endAssignment && operations.push(endAssignment(String(assignment.membershipId)))
        }
      })
    }

    const res = await Promise.all(operations)

    /*
     * Refetch explicitly here. We have two mutations for writing, but only one for reading.
     * So we cannot do the same `refetchQueries` option in the mutations because
     * Apollo will recognise if one is already in flight and not trigger another.
     * But we end with race conditions like:
     *   Send mutation request A
     *   Send mutation request B
     *   Receive response A
     *      -> Trigger refetchQuery C
     *   Receive response B
     *      -> Refetch in flight. Haven't received response C yet, but request already processed by server
     *   Receive response C. Does not contain results from mutation request B
     *   Show out of date data
     */
    refetch().then(() => {
      setSubmitting(false)
      if (res.every((result) => result === true)) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.employee_dimensions_info.modals.set_assignments.flash.success'),
        })
        onClose()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('finance.employee_dimensions_info.modals.set_assignments.flash.error'),
        })
      }
    })
  }

  const onChangeSingle = async (
    dimensionId: string,
    value: DimensionValue,
    membershipId?: string
  ) => {
    setAssignments(
      assignments.map((dimension) => {
        if (dimension.id == dimensionId && dimension.type == 'single-value') {
          return { ...dimension, assignmentValue: value, membershipId }
        } else {
          return dimension
        }
      })
    )
  }

  const onChangeMultiple = async (
    dimensionId: string,
    values: (DimensionValue & { percentage: number })[]
  ) => {
    setAssignments(
      assignments.map((dimension) => {
        if (dimension.id == dimensionId && dimension.type == 'multiple-value') {
          return { ...dimension, assignments: values }
        } else {
          return dimension
        }
      })
    )
  }

  const multipleAssignmentValueIsValid = ({
    assignments: assignmentValues,
  }: MutipleValueDimensionAssignment) => {
    if (assignmentValues.length == 0) {
      return true
    }

    const totalDedication = (
      assignmentValues.reduce((acc, cur) => acc + cur.percentage, 0) * 100
    ).toFixed(2)

    return totalDedication == '100.00' && !assignmentValues.some((a) => a.percentage == 0)
  }

  const assignmentsAreValid = assignments.every(
    (a) => a.type == 'single-value' || multipleAssignmentValueIsValid(a)
  )

  return {
    assignments,
    onSave,
    submitting,
    onChangeSingle,
    onChangeMultiple,
    allowSubmit:
      assignmentsAreValid &&
      !submitting &&
      (updatedAssignments.length > 0 || endedAssignments.length > 0),
  }
}

export default useAssignmentsPanel
